import { filter, find, map, propEq, propFromSelector, some } from '../utils';
import { property, curry } from 'lodash-es';
import { compose } from 'redux';
import { jsonApi } from '../http-request';
import { selectors as itemDefinition } from '../item-definition';

const collection = jsonApi.selectors.collection('site_configs');
const findResource = jsonApi.findResource(collection);

const findResourceAndHydrate = curry((fn, state) =>
  compose(
    jsonApi.utils.hydrate(state),
    findResource(fn)
  )(state)
);

const createFacade = selector => {
  const propFromResource = propFromSelector(selector);

  const channel = propFromResource('channel');
  const entities = propFromResource('entities');
  const isCompany = property('is_company');
  const isDivision = property('is_division');
  const isCompanyOrDivision = entity => isCompany(entity) || isDivision(entity);
  const division = compose(
    find(isDivision),
    entities
  );
  const itemDefinitions = compose(
    map(itemDefinition.byId),
    map(property('id')),
    propFromResource('item_definitions')
  );
  const brandedEntities = compose(
    filter(propEq('show_branding', true)),
    entities
  );
  const company = compose(
    find(isCompany),
    entities
  );
  const name = property('name');

  return {
    brandedCompaniesAndDivisions: compose(
      filter(isCompanyOrDivision),
      brandedEntities
    ),
    brandedProfessionals: compose(
      filter(entity =>
        some(fn => fn(entity), [
          propEq('kind', 'agent'),
          propEq('kind', 'cobranded_agent'),
        ])
      ),
      brandedEntities
    ),
    brandedEntities,
    channelClientName: compose(
      property('client_name'),
      channel
    ),
    channelDivisionName: compose(
      property('division_name'),
      channel
    ),
    company,
    companyName: compose(
      name,
      company
    ),
    companyDisplayName: compose(
      property('display_name'),
      company
    ),
    dashboardImageId: propFromResource('dashboard_image_id'),
    division,
    divisionName: compose(
      name,
      division
    ),
    entities,
    itemDefinitions,
    itemDefinitionsCodes: state =>
      compose(
        map(item => item.code(state)),
        itemDefinitions
      )(state),
    hasDivision: compose(
      Boolean,
      division
    ),
  };
};

const findFirst = (_, index) => index === 0;

// Using `first` here instead of `byId` so that we can still access the
// siteConfig when the user is signed out and there is no session
export const first = () => createFacade(findResourceAndHydrate(findFirst));
